/**
 * Timeline Lane Component
 *
 * Renders a horizontal timeline showing expressions with temporal_metadata.
 * Clicking a lane entry highlights the corresponding expression and graph nodes.
 *
 * @see docs/designs/smart-asset-algebra-visualization.md
 * @see docs/discoveries/2026-01-05-ui-schema-consumption.md
 */
import { memo, useMemo } from "react";
import type {
  ContractAlgebra,
  ContractExpression,
  TemporalMetadata,
} from "../../../types/valueFlows";
import { useHighlightOptional } from "./HighlightContext";

/**
 * Temporal entry extracted from contract algebra
 */
interface TemporalEntry {
  /** Expression ID */
  id: string;
  /** Expression definition */
  expression: ContractExpression;
  /** Temporal metadata */
  temporal: TemporalMetadata;
}

interface TimelineLaneProps {
  /** Contract algebra containing expressions with temporal_metadata */
  contractAlgebra?: ContractAlgebra;
  /** Compact mode for mobile/small screens */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Map milestone_icon to SVG glyph
 */
const MilestoneIcon = ({ icon, className }: { icon?: string; className?: string }) => {
  switch (icon) {
    case "calendar-check":
      return (
        <svg
          className={className}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="2" y="3" width="12" height="11" rx="1" />
          <path d="M2 6h12M5 1v3M11 1v3M5 10l2 2 4-4" />
        </svg>
      );
    case "calendar-x":
      return (
        <svg
          className={className}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="2" y="3" width="12" height="11" rx="1" />
          <path d="M2 6h12M5 1v3M11 1v3M6 9l4 4M10 9l-4 4" />
        </svg>
      );
    case "calendar":
      return (
        <svg
          className={className}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="2" y="3" width="12" height="11" rx="1" />
          <path d="M2 6h12M5 1v3M11 1v3" />
        </svg>
      );
    case "clock":
      return (
        <svg
          className={className}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <circle cx="8" cy="8" r="6" />
          <path d="M8 4v4l3 2" />
        </svg>
      );
    case "flag":
      return (
        <svg
          className={className}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M3 2v12M3 2l8 3-8 4" />
        </svg>
      );
    case "check":
      return (
        <svg
          className={className}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M3 8l3 3 7-7" />
        </svg>
      );
    default:
      // Default dot marker
      return (
        <svg className={className} viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="8" r="4" />
        </svg>
      );
  }
};

/**
 * Timeline entry component
 */
const TimelineEntry = memo(
  ({
    entry,
    isFirst,
    isLast,
    isHighlighted,
    onClick,
    compact,
  }: {
    entry: TemporalEntry;
    isFirst: boolean;
    isLast: boolean;
    isHighlighted: boolean;
    onClick: () => void;
    compact?: boolean;
  }) => {
    const { temporal, expression } = entry;

    return (
      <button
        type="button"
        className={`
          relative flex flex-col items-center group cursor-pointer
          ${compact ? "min-w-[60px]" : "min-w-[100px]"}
          ${isHighlighted ? "z-10" : ""}
        `}
        onClick={onClick}
      >
        {/* Connector line */}
        <div className="absolute top-[10px] left-0 right-0 h-0.5 bg-sa-border">
          {isFirst && <div className="absolute left-0 w-1/2 h-full bg-transparent" />}
          {isLast && <div className="absolute right-0 w-1/2 h-full bg-transparent" />}
        </div>

        {/* Milestone marker */}
        <div
          className={`
            relative z-10 flex items-center justify-center rounded-full
            transition-all duration-200
            ${compact ? "w-5 h-5" : "w-6 h-6"}
            ${
              isHighlighted
                ? "bg-primary text-white ring-2 ring-primary/40"
                : "bg-sa-surface-hover text-sa-text-muted group-hover:bg-sa-border"
            }
          `}
        >
          <MilestoneIcon
            icon={temporal.milestone_icon}
            className={compact ? "w-3 h-3" : "w-4 h-4"}
          />
        </div>

        {/* Label */}
        {!compact && (
          <div
            className={`
              mt-1 text-xs text-center leading-tight max-w-[90px] truncate
              ${isHighlighted ? "text-primary font-medium" : "text-sa-text-muted"}
            `}
            title={temporal.label}
          >
            {temporal.label}
          </div>
        )}

        {/* Operation badge */}
        {!compact && (
          <div
            className={`
              mt-0.5 text-[10px] px-1.5 py-0.5 rounded
              ${isHighlighted ? "bg-primary/10 text-primary" : "bg-sa-surface-hover text-sa-text-muted"}
            `}
          >
            {expression.op}
          </div>
        )}
      </button>
    );
  },
);

TimelineEntry.displayName = "TimelineEntry";

/**
 * Timeline Lane Component
 *
 * Displays temporal expressions as a horizontal timeline
 */
export const TimelineLane = memo(
  ({ contractAlgebra, compact = false, className = "" }: TimelineLaneProps) => {
    const highlightContext = useHighlightOptional();

    // Extract and sort temporal entries
    const temporalEntries = useMemo(() => {
      if (!contractAlgebra?.expressions) return [];

      const entries: TemporalEntry[] = [];

      for (const [id, expression] of Object.entries(contractAlgebra.expressions)) {
        if (expression.temporal_metadata) {
          entries.push({
            id,
            expression,
            temporal: expression.temporal_metadata,
          });
        }
      }

      // Sort by lane_position
      return entries.sort((a, b) => a.temporal.lane_position - b.temporal.lane_position);
    }, [contractAlgebra?.expressions]);

    // Handle entry click
    const handleEntryClick = (entry: TemporalEntry) => {
      if (!highlightContext) return;

      const nodeId = entry.expression.graph_binding?.node_id;
      const highlightGroup = entry.expression.graph_binding?.highlight_group;

      highlightContext.highlightFromTemporal(entry.id, nodeId, highlightGroup);
    };

    if (temporalEntries.length === 0) {
      return null;
    }

    return (
      <div
        className={`
          timeline-lane flex items-start gap-1 px-4 py-2
          bg-sa-surface-hover border-t border-sa-border
          overflow-x-auto
          ${className}
        `}
        data-testid="timeline-lane"
      >
        {/* Timeline label */}
        <div
          className={`
            flex-shrink-0 text-xs font-medium text-sa-text-muted
            ${compact ? "w-8" : "w-16"} pt-1
          `}
        >
          {compact ? "T" : "Timeline"}
        </div>

        {/* Timeline entries */}
        <div className="flex items-start gap-0 flex-1">
          {temporalEntries.map((entry, index) => (
            <TimelineEntry
              key={entry.id}
              entry={entry}
              isFirst={index === 0}
              isLast={index === temporalEntries.length - 1}
              isHighlighted={highlightContext?.isExpressionHighlighted(entry.id) ?? false}
              onClick={() => handleEntryClick(entry)}
              compact={compact}
            />
          ))}
        </div>
      </div>
    );
  },
);

TimelineLane.displayName = "TimelineLane";
